import { round } from 'lodash-es';
import { useMemo } from 'react';
import { CurrencyCell } from 'src/components/cells/CurrencyCell';
import { PercentCell } from 'src/components/cells/PercentCell';
import type { IBrandPerformanceMetricsDataTable } from 'src/queries';
import { useBrandPerformanceMetrics } from 'src/queries';
import type { FrontTableColumn, FrontTableProps } from 'src/ui-components';

export const useTableData = (): FrontTableProps<IBrandPerformanceMetricsDataTable> => {
  const { data, isLoading } = useBrandPerformanceMetrics();

  const columns: FrontTableColumn<IBrandPerformanceMetricsDataTable>[] = [
    { headerName: 'Date', field: 'date', flex: 1, minWidth: 150 },
    {
      headerName: 'Total Sales',
      field: 'total_sales',
      flex: 1,
      minWidth: 150,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    { headerName: 'PY Date', field: 'py_date', flex: 1, hidden: true },
    {
      headerName: 'Previous Year',
      field: 'py_total_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Change ($)',
      field: 'py_change_total_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Change (%)',
      field: 'py_change_pct_total_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <PercentCell value={value} colored decimal hideTrendIcon divide />,
    },
    {
      headerName: 'SNS Sales',
      field: 'sns_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY SNS Sales',
      field: 'py_sns_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY SNS Change ($)',
      field: 'py_change_sns_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY SNS Change (%)',
      field: 'py_change_pct_sns_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <PercentCell value={value} colored decimal hideTrendIcon divide />,
    },
    {
      headerName: 'Organic Sales',
      field: 'organic_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Organic Sales',
      field: 'py_organic_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Organic Change ($)',
      field: 'py_change_organic_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Organic Change (%)',
      field: 'py_change_pct_organic_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <PercentCell value={value} colored decimal hideTrendIcon divide />,
    },
    {
      headerName: 'Paid Sales',
      field: 'paid_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Paid Sales',
      field: 'py_paid_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Paid Change ($)',
      field: 'py_change_paid_sales',
      flex: 1,
      minWidth: 150,
      hidden: true,
      type: 'number',
      renderCell: ({ value }) => <CurrencyCell value={value} />,
    },
    {
      headerName: 'PY Paid Change (%)',
      field: 'py_change_pct_paid_sales',
      flex: 1,
      minWidth: 150,
      hidden: false,
      type: 'number',
      renderCell: ({ value }) => <PercentCell value={value} colored decimal hideTrendIcon divide />,
    },
  ];

  const rows = useMemo(
    () =>
      data?.data?.dataTable.map((r) => ({
        ...r,
        id: r.date,
        py_change_pct_paid_sales: round(r.py_change_pct_paid_sales * 100, 2),
        py_change_pct_total_sales: round(r.py_change_pct_total_sales * 100, 2),
        py_change_pct_sns_sales: round(r.py_change_pct_sns_sales * 100, 2),
        py_change_pct_organic_sales: round(r.py_change_pct_organic_sales * 100, 2),
      })) || [],
    [data?.data?.dataTable],
  );
  return {
    columns,
    data: rows,
    loading: isLoading,
    defaultSorting: { field: 'date', sort: 'desc' },
    toolbar: {
      showQuickFilter: true,
      showExportButton: true,
      showColumnSelector: true,
      showFilterButton: true,
    },
    pagination: true,
  };
};
